import { useMemo } from "react";
import Badge from "react-bootstrap/Badge";
import { useDispatch } from "react-redux";
import { resources } from "@constants/hardcoded-strings";
import { ENTITY_STATUS_OPTIONS } from "@constants/entity-status-options";
import { EntityStatusTypes } from "@enums/entity-status-types";
import { useTableFilters } from "@hooks/use-table-filters";
import { createColumnHelper, getCoreRowModel, getFilteredRowModel, getPaginationRowModel, getSortedRowModel, TableState, useReactTable } from "@tanstack/react-table";
import { sortStrings } from "@utils/string-util";
import { TableSettingsTableTypes } from "@enums/table-settings-table-types";
import { IdwTable } from "@containers/common/idw-table/idw-table";
import { PaginationContainer } from "@containers/common/pagination-container/pagination-container";
import { IdwTableContainer } from "@containers/common/idw-table-container/idw-table-container";
import { handleSetColumnFiltersChange, handleSetPaginationChange, handleSetSortingChange } from "@utils/react-table/react-table-util";
import styles from "./message-templates-table-container.less";
import { MessageTemplatesCell } from "@containers/message-templates/message-templates-table-cell/message-templates-table-cell";
import { FormattedMessageTemplate } from "@idw-types/data/message-template";
import { MESSAGE_TEMPLATE_TYPE_OPTIONS } from "@constants/message-template-constants";

type MessageTemplatesTableContainerProps = {
    messageTemplates: Array<FormattedMessageTemplate>;
    tableState: TableState;
    isPageHeight: boolean;
};

type MessageTemplatesTableDataRow = FormattedMessageTemplate & {
    companyName: string;
};

export const MessageTemplatesTableContainer = (
    props: MessageTemplatesTableContainerProps
) => {
    const dispatch = useDispatch();

    const { messageTemplates, tableState, isPageHeight } = props;

    const messageTemplateTableData: Array<MessageTemplatesTableDataRow> = useMemo(() => {
        return messageTemplates.map(template => ({
            ...template,
            companyName: template.company?.name || "IDDataweb (Public)"
        }));
    }, [messageTemplates]);

    const columnHelper = createColumnHelper<MessageTemplatesTableDataRow>();
    const columns = useMemo(() => ([
        columnHelper.accessor("name", {
            header: resources.tables.messageTemplates.columns.name.header,
            filterFn: "fuzzyText",
            cell: (props) => (
                <MessageTemplatesCell
                    value={props.getValue()}
                    messageTemplate={props.row.original}
                />
            ),
            sortingFn: (a, b) => sortStrings(a.original.name, b.original.name)
        }),
        columnHelper.accessor("type", {
            header: resources.tables.messagingTemplates.columns.type.header,
            filterFn: "singleSelectFilter",
            cell: (props) => <div>{props.row.original.type}</div>,
            meta: {
                filterOptions: MESSAGE_TEMPLATE_TYPE_OPTIONS
            },
        }),
        columnHelper.accessor("companyName", {
            header: resources.tables.messageTemplates.columns.organization.header,
            filterFn: "fuzzyText",
            sortingFn: (a, b) => sortStrings(a.original.companyName, b.original.companyName)
        }),
        columnHelper.accessor("status", {
            header: resources.common.labels.status,
            filterFn: "singleSelectFilter",
            meta: {
                filterOptions: ENTITY_STATUS_OPTIONS
            },
            cell: (props) => {
                switch (props.getValue()) {
                    case EntityStatusTypes.ACTIVE:
                        return <Badge bg={"success"}>{resources.common.active}</Badge>;
                    case EntityStatusTypes.INACTIVE:
                        return <Badge bg={"danger"}>{resources.common.inactive}</Badge>;
                    default:
                        return <span>{resources.common.notFound}</span>;
                }
            }
        })
    ]), [messageTemplateTableData]);

    const { filterTypes } = useTableFilters();

    const useTableResult = useReactTable({
        columns,
        data: messageTemplateTableData,
        state: tableState,
        filterFns: filterTypes,
        autoResetPageIndex: true,
        onSortingChange: (sortingOrUpdateFn) => handleSetSortingChange(sortingOrUpdateFn, TableSettingsTableTypes.MESSAGE_TEMPLATES, tableState.sorting, dispatch),
        onColumnFiltersChange: (filtersOrUpdateFn) => handleSetColumnFiltersChange(filtersOrUpdateFn, TableSettingsTableTypes.MESSAGE_TEMPLATES, tableState.columnFilters, dispatch),
        onPaginationChange: (paginationOrUpdateFn) => handleSetPaginationChange(paginationOrUpdateFn, TableSettingsTableTypes.MESSAGE_TEMPLATES, tableState.pagination, dispatch),
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel()
    });

    return (
        <IdwTableContainer className={styles.messageTemplatesTableContainer}>
            <IdwTable
                id="message-templates"
                useTableResult={useTableResult}
                headerColumnKeyPrefix={"message-templates"}
                className={styles.messageTemplatesTable}
            />
            <PaginationContainer
                idPrefix="message-templates"
                useTableResult={useTableResult}
                isPageHeight={isPageHeight}
            />
        </IdwTableContainer>
    );
};